import { Handle, Position } from '@xyflow/react';
import { Stack, Text, MultiSelect } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { useBusinessHours } from '../business-hours';
import { hoursOfOperationWidgetCore, type HoursOfOperationConfig } from '@legacy-shared/widgets/core/hoursOfOperation';

export { type HoursOfOperationConfig } from '@legacy-shared/widgets/core/hoursOfOperation';

function HoursOfOperationNode({ id, data, selected }: WidgetNodeProps<HoursOfOperationConfig>) {
  const count = data.config.businessHoursIds.length;
  const subtitle = count > 0 ? `${count} schedule${count > 1 ? 's' : ''}` : 'No schedules';

  return (
    <WidgetNodeShell
      icon={IconClock}
      title={data.label || 'Hours of Operation'}
      subtitle={subtitle}
      configured={count > 0}
      errors={data.errors}
      selected={selected}
      color="teal"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="open" label="Open" offset="35%" nodeId={id} />
      <LabeledHandle type="source" position="bottom" id="closed" label="Closed" offset="65%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function HoursOfOperationConfigPanel({ config, onChange }: ConfigPanelProps<HoursOfOperationConfig>) {
  const schedules = useBusinessHours();

  const options = (schedules ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Route calls based on whether any of the selected business hours schedules are currently open.
      </Text>

      <MultiSelect
        label="Business Hours Schedules"
        placeholder="Select schedules..."
        data={options}
        value={config.businessHoursIds}
        onChange={(ids) => onChange({ ...config, businessHoursIds: ids })}
        searchable
      />
    </Stack>
  );
}

export const hoursOfOperationWidget: WidgetDefinition<HoursOfOperationConfig> = {
  ...hoursOfOperationWidgetCore,
  icon: 'IconClock',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'open', type: 'success', label: 'Open', position: 'bottom' },
      { id: 'closed', type: 'failure', label: 'Closed', position: 'bottom' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: HoursOfOperationNode,
    configPanel: HoursOfOperationConfigPanel,
    preview: (config) => {
      const count = config.businessHoursIds.length;
      return count > 0 ? `${count} schedule${count > 1 ? 's' : ''}` : 'No schedules';
    },
  },

  validation: {
    ...hoursOfOperationWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
